import { useCallback, useEffect, useRef } from 'react';

import { useQueryClient } from '@tanstack/react-query';

import { useApiWebSocketConnection } from 'features/auth/hooks/useApiWebSocketConnection';

export interface SignalRQueryInvalidationOptions {
  // Entity context for filtering events
  patientId?: number;
  examId?: number;
  locationId?: number;
  worklistId?: number;

  // Query invalidation configuration
  invalidateOnFileChanged?: boolean | string[]; // true = invalidate all, array = specific query keys
  invalidateOnPatientChanged?: boolean | string[];
  invalidateOnExamChanged?: boolean | string[];
  invalidateOnWorklistChanged?: boolean | string[];
  invalidateOnLocationChanged?: boolean | string[];
  invalidateOnFormChanged?: boolean | string[];
  invalidateOnSystemDataChanged?: boolean | string[];

  // Advanced options
  enableDebugLogging?: boolean;
  debounceMs?: number; // Debounce invalidations to prevent rapid fire
  enableSmartInvalidation?: boolean; // Only invalidate relevant queries based on event payload
}

interface SignalREventPayload {
  patientId?: number;
  examId?: number;
  fileId?: number;
  locationId?: number;
  worklistId?: number;
  formId?: number;
  action?: string;
  status?: string;
  dataType?: string;
  timestamp?: string;
  userId?: string;
}

/**
 * Generic hook for integrating SignalR events with TanStack Query invalidation.
 * Automatically invalidates and refetches queries when relevant SignalR events are received.
 *
 * @example Basic usage:
 * ```typescript
 * useSignalRQueryInvalidation({
 *   patientId: 123,
 *   invalidateOnExamChanged: ['exams-grid', 'patient-exams'],
 *   invalidateOnFileChanged: true, // Invalidate all queries
 * });
 * ```
 *
 * @example Advanced usage with smart invalidation:
 * ```typescript
 * useSignalRQueryInvalidation({
 *   patientId: 123,
 *   examId: 456,
 *   invalidateOnExamChanged: ['exams-grid'],
 *   invalidateOnFileChanged: ['files-grid'],
 *   enableSmartInvalidation: true,
 *   debounceMs: 500,
 *   enableDebugLogging: true,
 * });
 * ```
 */
export function useSignalRQueryInvalidation(options: SignalRQueryInvalidationOptions = {}) {
  const connection = useApiWebSocketConnection();
  const queryClient = useQueryClient();
  const optionsRef = useRef(options);
  const debounceTimeoutRef = useRef<number | null>(null);

  // Keep options ref updated
  useEffect(() => {
    optionsRef.current = options;
  }, [options]);

  // Debounced invalidation function
  const invalidateQueries = useCallback(
    (queryKeys: string[] | boolean, eventType: string, payload?: SignalREventPayload) => {
      const { debounceMs = 100, enableDebugLogging } = optionsRef.current;

      if (debounceTimeoutRef.current) {
        window.clearTimeout(debounceTimeoutRef.current);
      }

      debounceTimeoutRef.current = window.setTimeout(() => {
        if (enableDebugLogging) {
          console.log(`[SignalR->TanStack] Invalidating queries for ${eventType}:`, {
            queryKeys,
            payload,
            isInvalidateAll: queryKeys === true,
          });
        }

        if (queryKeys === true) {
          // Invalidate all queries
          queryClient.invalidateQueries();
          if (enableDebugLogging) {
            console.log(`[SignalR->TanStack] ✅ Invalidated ALL queries for ${eventType}`);
          }
        } else if (Array.isArray(queryKeys)) {
          // Invalidate specific query keys
          queryKeys.forEach((queryKey) => {
            queryClient.invalidateQueries({ queryKey: [queryKey] });
            if (enableDebugLogging) {
              console.log(`[SignalR->TanStack] ✅ Invalidated query [${queryKey}] for ${eventType}`);
            }
          });
        }
      }, debounceMs);
    },
    [queryClient],
  );

  // Smart invalidation helper - only invalidates if event is relevant to current context
  const shouldInvalidateForEvent = useCallback((payload: SignalREventPayload | undefined, eventType: string): boolean => {
    const { patientId, examId, locationId, worklistId, enableSmartInvalidation, enableDebugLogging } = optionsRef.current;

    if (!enableSmartInvalidation) {
      return true; // Always invalidate if smart invalidation is disabled
    }

    if (!payload) {
      return true; // No payload means global event, always invalidate
    }

    // Check if event is relevant to current context
    const isRelevant =
      (patientId === undefined || payload.patientId === undefined || payload.patientId === patientId) &&
      (examId === undefined || payload.examId === undefined || payload.examId === examId) &&
      (locationId === undefined || payload.locationId === undefined || payload.locationId === locationId) &&
      (worklistId === undefined || payload.worklistId === undefined || payload.worklistId === worklistId);

    if (enableDebugLogging) {
      console.log(`[SignalR->TanStack] Smart invalidation check for ${eventType}:`, {
        payload,
        currentContext: { patientId, examId, locationId, worklistId },
        isRelevant,
      });
    }

    return isRelevant;
  }, []);

  // Event handlers
  const handleFileChanged = useCallback(
    (payload: SignalREventPayload) => {
      const { invalidateOnFileChanged } = optionsRef.current;
      if (invalidateOnFileChanged && shouldInvalidateForEvent(payload, 'fileChanged')) {
        invalidateQueries(invalidateOnFileChanged, 'fileChanged', payload);
      }
    },
    [invalidateQueries, shouldInvalidateForEvent],
  );

  const handlePatientChanged = useCallback(
    (payload: SignalREventPayload) => {
      const { invalidateOnPatientChanged } = optionsRef.current;
      if (invalidateOnPatientChanged && shouldInvalidateForEvent(payload, 'patientChanged')) {
        invalidateQueries(invalidateOnPatientChanged, 'patientChanged', payload);
      }
    },
    [invalidateQueries, shouldInvalidateForEvent],
  );

  const handleExamChanged = useCallback(
    (payload: SignalREventPayload) => {
      const { invalidateOnExamChanged } = optionsRef.current;
      if (invalidateOnExamChanged && shouldInvalidateForEvent(payload, 'examChanged')) {
        invalidateQueries(invalidateOnExamChanged, 'examChanged', payload);
      }
    },
    [invalidateQueries, shouldInvalidateForEvent],
  );

  const handleWorklistChanged = useCallback(
    (payload: SignalREventPayload) => {
      const { invalidateOnWorklistChanged } = optionsRef.current;
      if (invalidateOnWorklistChanged && shouldInvalidateForEvent(payload, 'worklistUpdated')) {
        invalidateQueries(invalidateOnWorklistChanged, 'worklistUpdated', payload);
      }
    },
    [invalidateQueries, shouldInvalidateForEvent],
  );

  const handleLocationChanged = useCallback(
    (payload: SignalREventPayload) => {
      const { invalidateOnLocationChanged } = optionsRef.current;
      if (invalidateOnLocationChanged && shouldInvalidateForEvent(payload, 'locationChanged')) {
        invalidateQueries(invalidateOnLocationChanged, 'locationChanged', payload);
      }
    },
    [invalidateQueries, shouldInvalidateForEvent],
  );

  const handleFormChanged = useCallback(
    (payload: SignalREventPayload) => {
      const { invalidateOnFormChanged } = optionsRef.current;
      if (invalidateOnFormChanged && shouldInvalidateForEvent(payload, 'formChanged')) {
        invalidateQueries(invalidateOnFormChanged, 'formChanged', payload);
      }
    },
    [invalidateQueries, shouldInvalidateForEvent],
  );

  const handleSystemDataChanged = useCallback(
    (payload: SignalREventPayload) => {
      const { invalidateOnSystemDataChanged } = optionsRef.current;
      if (invalidateOnSystemDataChanged && shouldInvalidateForEvent(payload, 'systemDataChanged')) {
        invalidateQueries(invalidateOnSystemDataChanged, 'systemDataChanged', payload);
      }
    },
    [invalidateQueries, shouldInvalidateForEvent],
  );

  // Register SignalR event listeners
  useEffect(() => {
    if (!connection) {
      if (options.enableDebugLogging) {
        console.log('[SignalR->TanStack] No connection available for query invalidation');
      }
      return;
    }

    if (options.enableDebugLogging) {
      console.log('[SignalR->TanStack] Registering query invalidation listeners:', {
        patientId: options.patientId,
        examId: options.examId,
        locationId: options.locationId,
        worklistId: options.worklistId,
        enableSmartInvalidation: options.enableSmartInvalidation,
        debounceMs: options.debounceMs,
      });
    }

    // Register event listeners
    connection.on('fileChanged', handleFileChanged);
    connection.on('patientChanged', handlePatientChanged);
    connection.on('examChanged', handleExamChanged);
    connection.on('worklistUpdated', handleWorklistChanged);
    connection.on('locationChanged', handleLocationChanged);
    connection.on('formChanged', handleFormChanged);
    connection.on('systemDataChanged', handleSystemDataChanged);

    // Legacy event support
    connection.on('patientFileUploaded', () => handleFileChanged({}));
    connection.on('dataUpdated', () => handleSystemDataChanged({}));

    if (options.enableDebugLogging) {
      console.log('[SignalR->TanStack] ✅ Query invalidation listeners registered');
    }

    return () => {
      if (options.enableDebugLogging) {
        console.log('[SignalR->TanStack] Cleaning up query invalidation listeners');
      }

      connection.off('fileChanged', handleFileChanged);
      connection.off('patientChanged', handlePatientChanged);
      connection.off('examChanged', handleExamChanged);
      connection.off('worklistUpdated', handleWorklistChanged);
      connection.off('locationChanged', handleLocationChanged);
      connection.off('formChanged', handleFormChanged);
      connection.off('systemDataChanged', handleSystemDataChanged);

      // Legacy event cleanup
      connection.off('patientFileUploaded');
      connection.off('dataUpdated');

      // Clear any pending debounced invalidations
      if (debounceTimeoutRef.current) {
        window.clearTimeout(debounceTimeoutRef.current);
        debounceTimeoutRef.current = null;
      }
    };
  }, [
    connection,
    handleFileChanged,
    handlePatientChanged,
    handleExamChanged,
    handleWorklistChanged,
    handleLocationChanged,
    handleFormChanged,
    handleSystemDataChanged,
    options.patientId,
    options.examId,
    options.locationId,
    options.worklistId,
    options.enableDebugLogging,
  ]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        window.clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  // Return utility functions for manual invalidation
  return {
    invalidateQueries: (queryKeys: string[] | boolean, reason?: string) => {
      invalidateQueries(queryKeys, reason || 'manual', undefined);
    },
    isConnected: !!connection,
    connectionState: connection?.state,
  };
}
